import * as Panel2 from '@wandb/weave/components/Panel2/panel';
import {PanelComp2} from '@wandb/weave/components/Panel2/PanelComp';
import {PanelContextProvider} from '@wandb/weave/components/Panel2/PanelContext';
import {Spec as PanelExpressionSpec} from '@wandb/weave/components/Panel2/PanelExpression';
import {
  constBoolean,
  constNodeUnsafe,
  constNumber,
  constString,
  list,
  MAX_RUN_LIMIT,
  maybe,
  Node,
  opArray,
  opConcat,
  opLimit,
  opProjectFilteredRuns,
  opRootProject,
  voidNode,
} from '@wandb/weave/core';
import {produce} from 'immer';
import * as _ from 'lodash';
import React, {useCallback, useMemo, useState} from 'react';

import {toRunsDataQuery} from '../../containers/RunsDataLoader';
import {useDeepMemo, useGatedValue} from '../../state/hooks';
import {useRunsData} from '../../state/runs/hooks';
import * as ColorUtil from '../../util/colors';
import * as Filter from '../../util/filters';
import {PanelProps} from '../../util/panelProps';
import * as QueryTS from '../../util/queryts';
import * as QueryTypes from '../../util/queryTypes';
import {lookupRunset} from '../../util/runs';
import {useColorMode} from '../../util/useColorMode';
import {ReversibleWeaveErrorBoundary} from '../Panel2/ErrorBoundary';
import {useWeaveInit} from '../panels.domain/weaveInit';
import {useWorkspaceSettingsColorPalette} from '../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {InstrumentedCenteredWaveLoader as Loader} from './../../components/utility/InstrumentedLoader';
import {WeaveConfig, weavePanelForWeaveKey} from './common';

type PanelWeaveProps = PanelProps<WeaveConfig>;

/** Convert old-style pageQuery to Weave query
 *
 * An old-style pageQuery is what we use everywhere else in the app to fetch
 * run data, controlled via runsets.
 *
 * Caveats:
 *   - Uses opProjectFilteredRuns, a non-public op! We need to fix
 *     this before exporting subparts of panels in reports. The filter
 *     for opProjectFitleredRuns is an old-style filter, not editable
 *     by the expression editor.
 *     TODO: Fix this to use opProjectRuns.filter (and fix gql.ts to push
 *       weave filters down to graphql), before enabling subpanel export.
 *   - Does not convert grouped runSets to grouped Weave queries.
 *     TODO: Do this. But we'll need to figure out how to make it performant.
 *   - limited to 25 runs for performance for now.
 */
function runNodeFromPageQuery(pageQuery: QueryTypes.Query) {
  const runSetNodes: Node[] = [];
  if (pageQuery.runSets != null) {
    for (const runSet of pageQuery.runSets) {
      if (runSet.enabled) {
        let filters = Filter.And([runSet.filters, pageQuery.filters]);
        if (runSet.selections != null) {
          filters = Filter.And([filters, runSet.selections]);
        }
        const sort = QueryTS.sortFromJSONSafe(runSet.sort);
        const runs = opProjectFilteredRuns({
          project: opRootProject({
            entityName: constString(runSet.entityName ?? pageQuery.entityName),
            projectName: constString(
              runSet.projectName ?? pageQuery.projectName
            ),
          }),
          filter: constString(
            JSON.stringify(Filter.toMongo(Filter.simplifyFilters(filters)))
          ),
          order: constString(QueryTS.sortToOrderString(sort)),
        });
        runSetNodes.push(runs);
      }
    }
  } else {
    let filters = pageQuery.filters;
    if (pageQuery.selections != null) {
      filters = Filter.And([filters, pageQuery.selections]);
    }
    const sort = QueryTS.sortFromJSONSafe(pageQuery.sort);
    const runs = opProjectFilteredRuns({
      project: opRootProject({
        entityName: constString(pageQuery.entityName),
        projectName: constString(pageQuery.projectName),
      }),
      filter: constString(
        JSON.stringify(Filter.toMongo(Filter.simplifyFilters(filters)))
      ),
      order: constString(QueryTS.sortToOrderString(sort)),
    });
    runSetNodes.push(runs);
  }
  if (runSetNodes.length === 0) {
    return constNodeUnsafe(list('run'), []);
  } else if (runSetNodes.length === 1) {
    // This will be the case when we are loading a single run workspace.
    // Setting limit to 1 allows us to make better decisions downstream -
    // in particular when displaying multi-table views.
    const limit = pageQuery.runName != null ? 1 : MAX_RUN_LIMIT;
    return opLimit({
      arr: runSetNodes[0],
      limit: constNumber(limit),
    });
  } else {
    const arrayArgs: {[index: string]: Node} = {};
    runSetNodes.forEach((node, i) => (arrayArgs[i] = node));
    return opLimit({
      arr: opConcat({arr: opArray(arrayArgs as any)}),
      limit: constNumber(MAX_RUN_LIMIT),
    });
  }
}

function firstProjectNodeFromPageQuery(pageQuery: QueryTypes.Query) {
  if (pageQuery.runSets != null) {
    for (const runSet of pageQuery.runSets) {
      if (runSet.enabled) {
        return opRootProject({
          entityName: constString(runSet.entityName ?? pageQuery.entityName),
          projectName: constString(runSet.projectName ?? pageQuery.projectName),
        });
      }
    }
  } else {
    return opRootProject({
      entityName: constString(pageQuery.entityName),
      projectName: constString(pageQuery.projectName),
    });
  }
  return constNodeUnsafe(maybe('project'), null);
}

const gatedValueStyle = {height: '100%', overflow: 'hidden', width: '100%'};

const PanelWeave: React.FC<PanelWeaveProps> = props => {
  useWeaveInit();
  const defaultConfig = useMemo(() => {
    if (props.config.defaultWorkspaceState?.panel2Config != null) {
      return {
        defaultWorkspaceState: props.config.defaultWorkspaceState,
        panel2Config: props.config.defaultWorkspaceState.panel2Config,
      };
    } else {
      // Legacy
      return (props.config as any).key != null
        ? weavePanelForWeaveKey((props.config as any).key, '')
        : null;
    }
  }, [props.config]);
  return (
    <ReversibleWeaveErrorBoundary
      {...props}
      innerComponent={PanelWeaveInner}
      sourcePanel={'PanelWeave'}
      hardResetValue={defaultConfig}
      panelConfigKey="panel2Config"
    />
  );
};

export default PanelWeave;

const PanelWeaveInner: React.FC<PanelWeaveProps> = props => {
  const {config, updateConfig} = props;
  const memoedPageQuery = useDeepMemo(props.pageQuery);

  const pageQueryWithSummaryKeys = useMemo(
    () =>
      produce(memoedPageQuery, draft => {
        // We need to fetch extra fields for summary keys, since the values
        // of summary keys are not in props.pageQuery and we need them to
        // calculate the correct runId for the run. This ensures that
        // PanelPlot run colors match the colors of run groups in PanelWeave.
        const summaryKeys = [];

        for (const rs of draft.runSets ?? []) {
          if (rs.grouping != null) {
            summaryKeys.push(
              ...rs.grouping
                .filter(f => f.section === 'summary')
                .map(f => f.name)
            );
          }
        }

        draft.summaryKeys = _.uniq(
          (draft.summaryKeys ?? []).concat(summaryKeys)
        );
      }),
    [memoedPageQuery]
  );

  const ungroupedPageQuery = useMemo(() => {
    return {
      ...pageQueryWithSummaryKeys,
      runSets: pageQueryWithSummaryKeys.runSets?.map(runSet => {
        return {...runSet, grouping: []};
      }),
    };
  }, [pageQueryWithSummaryKeys]);

  // We need to use an old-style RunsDataQuery to get the runColors
  // for now.
  const runsDataQuery = useMemo(
    () =>
      toRunsDataQuery(
        ungroupedPageQuery,
        {
          selectionsAsFilters: true,
        },
        {page: {size: 100}}
      ),
    [ungroupedPageQuery]
  );
  const {loading, data} = useRunsData(runsDataQuery);
  const colorPaletteOption = useWorkspaceSettingsColorPalette();
  // Make runColors available as a variable in the panel context.
  const runColors = useDeepMemo(
    useMemo(
      () =>
        _.fromPairs(
          data.filtered.map(resultRun => [
            resultRun.name,
            ColorUtil.runColor({
              run: resultRun,
              groupKeys: lookupRunset(resultRun, pageQueryWithSummaryKeys)
                ?.grouping,
              customRunColors: props.customRunColors,
              colorPaletteOption,
            }),
          ])
        ),
      [
        data.filtered,
        props.customRunColors,
        pageQueryWithSummaryKeys,
        colorPaletteOption,
      ]
    )
  );

  const singleRunWorkspace = ungroupedPageQuery.runName != null;

  const contextVars = useMemo(
    () => ({
      runColors: constNodeUnsafe(
        {type: 'dict', objectType: 'string'},
        runColors
      ),
      project: firstProjectNodeFromPageQuery(pageQueryWithSummaryKeys),
      ...(props.customRunNames && {
        customRunNames: constNodeUnsafe(
          {type: 'dict', objectType: 'string'},
          props.customRunNames
        ),
      }),
      singleRunWorkspace: constBoolean(singleRunWorkspace),
    }),
    [
      runColors,
      pageQueryWithSummaryKeys,
      singleRunWorkspace,
      props.customRunNames,
    ]
  );

  const run = useMemo(
    () => (loading ? voidNode() : runNodeFromPageQuery(ungroupedPageQuery)),
    [loading, ungroupedPageQuery]
  );

  const [context, setContext] = useState<Panel2.PanelContext>({});
  // TODO: any
  const updateContext = useCallback<Panel2.UpdateContext>(
    newContext => {
      setContext({...context, ...newContext});
    },
    [context]
  );

  const panel2Config = Panel2.useConfigChild(
    'panel2Config',
    config,
    updateConfig,
    {}
  );

  const wrapperRef = React.useRef<HTMLDivElement>(null);

  // This prevents the whole panel grid from being deleted in reports
  // when the user presses backspace. We do the same thing in weave-panels.tsx
  React.useEffect(() => {
    const el = wrapperRef.current;
    if (!el) {
      return;
    }

    const onDOMBeforeInput = (e: Event) => {
      e.stopPropagation();
    };

    el.addEventListener('beforeinput', onDOMBeforeInput);

    return () => {
      el.removeEventListener('beforeinput', onDOMBeforeInput);
    };
  }, []);
  const {isDarkMode} = useColorMode();

  return useGatedValue(
    <div ref={wrapperRef} style={gatedValueStyle}>
      {loading ? (
        <Loader name="weave-panel-loader" size="huge" />
      ) : (
        run.nodeType !== 'void' && (
          <PanelContextProvider newVars={contextVars}>
            <PanelComp2
              input={run}
              inputType={run.type}
              loading={false}
              panelSpec={PanelExpressionSpec}
              configMode={false}
              context={context}
              config={panel2Config.config}
              updateConfig={panel2Config.updateConfig}
              updateContext={updateContext}
              noPanelControls
              isDarkMode={isDarkMode}
            />
          </PanelContextProvider>
        )
      )}
    </div>,
    o => !loading
  );
};
